import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SmoothScroll } from "@/components/SmoothScroll";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Listings from "@/pages/Listings";
import OfficeListings from "@/pages/OfficeListings";
import About from "@/pages/About";
import Process from "@/pages/Process";
import Contact from "@/pages/Contact";
import Book from "@/pages/Book";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

// Flip to false to bring the site back online. Requires a redeploy to take effect.
const MAINTENANCE_MODE = false;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
};

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
  </>
);

const ProtectedRoute = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center bg-navy font-mono text-white/60 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  if (MAINTENANCE_MODE) {
    // Truly blank white page — no logo, no text, no scripts running visibly.
    // Admin login is still reachable directly at /admin/login if needed.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      return (
        <div className="App">
          <AuthProvider>
            <BrowserRouter>
              <Toaster position="top-right" richColors />
              <Routes>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/admin/login" replace />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </div>
      );
    }
    return <div data-testid="maintenance-blank" style={{ minHeight: "100vh", background: "#ffffff" }} />;
  }

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <SmoothScroll>
            <ScrollToTop />
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/listings" element={<PublicLayout><Listings /></PublicLayout>} />
              <Route path="/office-listings" element={<PublicLayout><OfficeListings /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/process" element={<PublicLayout><Process /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
              <Route path="/book" element={<PublicLayout><Book /></PublicLayout>} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </SmoothScroll>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
